import React, { useEffect } from 'react';
import { TDR } from 'tdr-common';
import { useTranslation } from 'react-i18next';
import { analytics, MODAL_OPENED_EVENT } from '../../../../helpers/analytics';
import { Modal, ModalControlProps } from '../modal';
import { PricingItem } from './pricing-item';
import { getTaxBreakdown } from '../../../checkout/util';
import { useRestaurantTaxPolicies } from '@/api/getRestaurantTaxPolicies';
import * as Dialog from '@radix-ui/react-dialog';
import { HeaderText } from '../../typography/header';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import closeIcon from '../../../../icons/close.svg';

type PricingBreakdownProps = {
	estimatedInvoice: Partial<TDR.Invoice>;
	timeSlot: TDR.TimeSlot,
	guestCount: number,
	restaurant: Partial<TDR.Restaurant>
};

type PricingBreakdownWithControlProps = PricingBreakdownProps & ModalControlProps;

// Component for use with ModalWithTrigger (no modal controls needed)
export const PricingBreakdown = ({ estimatedInvoice, timeSlot, guestCount, restaurant }: PricingBreakdownProps) => {
	const { t } = useTranslation();

	const depositLabel =
		estimatedInvoice?.lineItems?.flatMap(item => item.charges || []).find(charge => charge.target === 'deposit')?.label
		|| t('checkout.modals.pricing_breakdown.deposit');

	const subtotal = estimatedInvoice?.subtotal || 0;
	const deposit = estimatedInvoice?.deposit || 0;
	const currency = estimatedInvoice?.currency;
	const { data: taxPolicies = [] } = useRestaurantTaxPolicies(restaurant);
	const taxBreakdown = getTaxBreakdown(estimatedInvoice as TDR.Invoice, taxPolicies);

	// Calculate the unit price when isPerGuest is true
	const getTableSelectionAmount = () => {
		if (timeSlot?.isPerGuest && estimatedInvoice?.lineItems?.length > 0) {
			// Find the first line item with charges to get the unit price
			const firstLineItem = estimatedInvoice.lineItems[0];
			if (firstLineItem?.charges?.length > 0) {
				// Return the unit price (amount) instead of the total (absAmount)
				return firstLineItem.charges[0].amount;
			}
		}
		// Fall back to subtotal for non-per-guest pricing
		return subtotal;
	};

	// Custom header that works with Radix Dialog
	const CustomHeader = () => (
		<div className='flex justify-between p-2 bg-tablz-dark-gray-1 items-center border-b border-tablz-med-gray-4 min-h-[52px]'>
			<div className="opacity-0 cursor-default pointer-events-none p-4 rounded-2xl flex justify-center items-center bg-tablz-med-gray-2 border border-tablz-med-gray-4">
				<img src={closeIcon} alt="" width={20} height={20} />
			</div>
			<header className="w-full flex justify-center items-center">
				<VisuallyHidden>
					<Dialog.Title>{t('checkout.modals.pricing_breakdown.title')}</Dialog.Title>
				</VisuallyHidden>
				<HeaderText>{t('checkout.modals.pricing_breakdown.title')}</HeaderText>
			</header>
			<Dialog.Close asChild>
				<div className="p-4 rounded-2xl flex justify-center items-center bg-tablz-med-gray-2 cursor-pointer border border-tablz-med-gray-4" onClick={(e) => e.stopPropagation()}>
					<img src={closeIcon} alt="Close modal" width={20} height={20} />
				</div>
			</Dialog.Close>
		</div>
	);

	return (
		<div onClick={(e) => e.stopPropagation()}>
			<CustomHeader />
			<Modal.Body>
				<VisuallyHidden>
					<Dialog.Description>
						Detailed breakdown of pricing for this table reservation
					</Dialog.Description>
				</VisuallyHidden>
				<div className="flex flex-col gap-3">
					<PricingItem
						header={t('checkout.modals.pricing_breakdown.table_selection_fee')}
						description={t('checkout.modals.pricing_breakdown.table_selection_fee_description')}
						amount={getTableSelectionAmount()}
						currency={currency}
						guestCount={timeSlot?.isPerGuest ? guestCount : 0}
					/>
					{deposit > 0 ? (
						<PricingItem header={depositLabel} amount={deposit} currency={currency} />
					) : null}
					{taxBreakdown.filter(tax => tax.amount > 0).map((tax, index) =>
						(
							<PricingItem
								key={tax.taxId || index}
								header={`${tax.name} (${tax.percentage.toFixed(tax.percentage % 1 === 0 ? 0 : 3)}%)`}
								amount={tax.amount}
								currency={currency}
							/>
						)
					)}
					<PricingItem
						header={t('checkout.modals.pricing_breakdown.total')}
						amount={estimatedInvoice?.total}
						className="font-semibold border-none"
						currency={currency}
					/>
				</div>
			</Modal.Body>
		</div>
	);
};

// Legacy component for backward compatibility with existing modal pattern
export const PricingBreakdownModal = ({ open, onOpenChange, estimatedInvoice, timeSlot, guestCount, restaurant }: PricingBreakdownWithControlProps) => {
	useEffect(() => {
		if (open) {
			analytics.track(MODAL_OPENED_EVENT, { title: 'Pricing Breakdown Modal' });
		}
	}, [open]);

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<PricingBreakdown
				estimatedInvoice={estimatedInvoice}
				timeSlot={timeSlot}
				guestCount={guestCount}
				restaurant={restaurant}
			/>
		</Modal>
	);
};
